import { sortedIndexBy } from "lodash";
import { equals, UserProfile } from "../userProfile";
import { ChatItemStatus, IChatItem, IGroupableItem, isGroupableItem } from "./chatItems";

export class ChatItemGroup<T extends IGroupableItem> implements IGroupableItem {
  public static readonly tag = "chat-item-group";

  public readonly tag = ChatItemGroup.tag;
  private _startTimestamp: number;
  private _endTimestamp: number;
  private threshold: number;

  public readonly sortedItems: T[] = [];
  public readonly id: string;
  public readonly itemsTag: string;
  public readonly user: UserProfile;
  public readonly status: ChatItemStatus;

  constructor(chatItem: T, thresholdInSeconds = 600) {
    if (!isGroupableItem(chatItem)) {
      throw new Error(`${(chatItem as IGroupableItem).tag} cannot create a group`);
    }
    this.sortedItems.push(chatItem);
    this.id = `group-${chatItem.id}`;
    this.itemsTag = chatItem.tag;
    this.user = chatItem.user;
    this.status = chatItem.status;
    this._startTimestamp = chatItem.timestamp;
    this._endTimestamp = chatItem.timestamp;
    this.threshold = thresholdInSeconds * 1000;
  }

  public get items(): ReadonlyArray<T> {
    return this.sortedItems;
  }

  public get timestamp() {
    return this._endTimestamp;
  }

  public get startTimestamp() {
    return this._startTimestamp;
  }

  public get endTimestamp() {
    return this._endTimestamp;
  }

  public canBeAdded(item: T): boolean {
    return (
      isGroupableItem(item) &&
      equals(item.user, this.user) &&
      item.status === this.status &&
      item.timestamp >= this.startTimestamp &&
      item.timestamp <= this.startTimestamp + this.threshold
    );
  }

  public add(item: T): boolean {
    if (!this.canBeAdded(item)) {
      return false;
    }

    this.sortedItems.splice(sortedIndexBy(this.sortedItems, item, "timestamp"), 0, item);
    this._startTimestamp = this.sortedItems[0]!.timestamp;
    this._endTimestamp = this.sortedItems[this.sortedItems.length - 1]!.timestamp;

    return true;
  }

  public static isChatItemGroup = (m: IChatItem): m is ChatItemGroup<IGroupableItem> =>
    m.tag === ChatItemGroup.tag;
}
